
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, InputNumber, Row, Col, message } from 'antd';
import dayjs from 'dayjs';
import supplyChainService, { InboundShipment, ShipmentBatch, LogisticsProvider } from '../../../../services/supplyChainService';

interface ShipmentFormProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    initialValues?: InboundShipment;
}

const { Option } = Select;

export const ShipmentForm: React.FC<ShipmentFormProps> = ({ visible, onCancel, onSuccess, initialValues }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    // Dynamic Data
    const [batches, setBatches] = useState<ShipmentBatch[]>([]);
    const [providers, setProviders] = useState<LogisticsProvider[]>([]);
    const [shops, setShops] = useState<any[]>([]);

    useEffect(() => {
        if (visible) {
            fetchDependencies();
            if (initialValues) {
                form.setFieldsValue({
                    ...initialValues,
                    shipment_date: initialValues.shipment_date ? dayjs(initialValues.shipment_date) : null,
                    estimated_arrival_date: initialValues.estimated_arrival_date ? dayjs(initialValues.estimated_arrival_date) : null,
                    actual_arrival_date: initialValues.actual_arrival_date ? dayjs(initialValues.actual_arrival_date) : null,
                    shelf_date: initialValues.shelf_date ? dayjs(initialValues.shelf_date) : null,
                });
            } else {
                form.resetFields();
                form.setFieldsValue({
                    shipment_status: 'WORKING',
                    label_prep_type: 'SELLER_LABEL',
                    total_weight_kg: 0,
                    total_volume_cbm: 0
                });
            }
        }
    }, [visible, initialValues]);

    const fetchDependencies = async () => {
        try {
            const [batchRes, provRes, shopRes] = await Promise.all([
                supplyChainService.getShipmentBatches(),
                supplyChainService.getLogisticsProviders(),
                supplyChainService.getShops()
            ]);
            setBatches(Array.isArray(batchRes.data) ? batchRes.data : []);
            setProviders(Array.isArray(provRes.data) ? provRes.data : (provRes.data as any).results || []);
            setShops(Array.isArray(shopRes.data) ? shopRes.data : (shopRes.data as any).results || []);
        } catch (e) {
            console.error('Failed to load form dependencies', e);
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);

            // Format dates
            const payload = {
                ...values,
                shipment_date: values.shipment_date?.format('YYYY-MM-DD'),
                estimated_arrival_date: values.estimated_arrival_date?.format('YYYY-MM-DD'),
                actual_arrival_date: values.actual_arrival_date?.format('YYYY-MM-DD'),
                shelf_date: values.shelf_date?.format('YYYY-MM-DD'),
            };

            if (initialValues?.id) {
                await supplyChainService.updateInboundShipment(initialValues.id, payload);
                message.success('Shipment updated');
            } else {
                await supplyChainService.createInboundShipment(payload);
                message.success('Shipment created');
            }
            onSuccess();
        } catch (e) {
            message.error('Operation failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={visible}
            title={initialValues ? "Edit Shipment" : "Create Shipment"}
            onCancel={onCancel}
            onOk={handleOk}
            confirmLoading={submitting}
            width={800}
        >
            <Form form={form} layout="vertical">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="shipment_id" label="Shipment ID" rules={[{ required: true }]}>
                            <Input placeholder="FBA1234567" disabled={!!initialValues} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="shop" label="Shop" rules={[{ required: true }]}>
                            <Select placeholder="Select Shop">
                                {shops.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="shipment_name" label="Shipment Name">
                            <Input placeholder="e.g. US-WEST-001" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="destination_fulfillment_center_id" label="Destination FC">
                            <Input placeholder="e.g. LAX9" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="batch" label="Logistics Batch">
                            <Select placeholder="Assign to Batch" allowClear>
                                {batches.map(b => <Option key={b.id} value={b.id}>{b.batch_number} ({b.carrier})</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="shipment_status" label="Status">
                            <Select>
                                <Option value="WORKING">Working</Option>
                                <Option value="SHIPPED">Shipped</Option>
                                <Option value="RECEIVING">Receiving</Option>
                                <Option value="CLOSED">Closed</Option>
                                <Option value="CANCELLED">Cancelled</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <div style={{ borderTop: '1px solid #f0f0f0', margin: '16px 0', paddingTop: 16 }}>
                    <strong>Logistics Details</strong>
                </div>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="logistics_provider" label="Logistics Provider">
                            <Select placeholder="Provider" allowClear>
                                {providers.map(p => <Option key={p.id} value={p.id}>{p.name}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="logistics_channel" label="Channel">
                            <Input placeholder="e.g. Matson CLX" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="carrier_tracking_number" label="Tracking #">
                            <Input placeholder="Tracking Number" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="shipment_date" label="Shipped Date (T)">
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="estimated_arrival_date" label="ETA (U)">
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="actual_arrival_date" label="Actual Arrival (V)">
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="shelf_date" label="Shelf Date (W)">
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="total_weight_kg" label="Total Weight (kg)">
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="total_volume_cbm" label="Total Volume (cbm)">
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};
